// pages/worktrees.js — Worktrees page.
//
// Owns the `data-page="worktrees"` section. Lists per-agent git
// worktrees with active/released chips and disk-usage summary.
// Cloud-mode forks to renderCloudWorktrees (defined below).
import { state } from "../state.js";
import { worktreesState } from "../page-state.js";
import { patchHTML } from "../dom.js";
import { escapeHTML, ago, fmtDiskMB } from "../util.js";
import { isCloudMode, showToast } from "../ui.js";
import { api, apiFetch } from "../api.js";
import { decryptHint } from "../crypto.js";
import { fetchWorktrees } from "../fetchers.js";
import { withBuFilter } from "../bu-switcher.js";

// isDiscoveredWorktree: true when AC did not create this worktree (found by
// the daemon's git worktree scan). Prefers the API's explicit `discovered`
// flag, then the durable "wt_disc_" id prefix. NOT agent_id ===
// "ac:discovered" alone: a discovered worktree is handed to the real agent
// working in it once one is seen, so its agent_id changes — and team-mode
// rows (via teamsync) don't carry the `discovered` flag at all.
function isDiscoveredWorktree(w) {
  if (w.discovered === true) return true;
  const id = w.worktree_id || w.WorktreeID || "";
  return id.startsWith("wt_disc_") || w.agent_id === "ac:discovered";
}

// STALE_THRESHOLD_DAYS: how many days without a commit before a
// worktree is bucketed as "stale". Was 24h originally, which
// over-flagged actively-used worktrees where the user reads/edits
// for days before the first commit (very common for arc-style
// sessions). 7d matches the natural shape of the data and means
// "you almost certainly forgot this one exists" rather than "no
// commit today." activityBadgeHTML below uses the same threshold.
const STALE_THRESHOLD_DAYS = 7;

// hideStale toggles whether the cloud-mode renderer filters out
// "stale" rows. Default = true because the wire's `active=true`
// filter only excludes formally-released worktrees; the page still
// inherits a long tail of long-dead SessionStart allocations.
// localStorage-persisted so the user's choice survives reloads.
let hideStale = (() => {
  const v = localStorage.getItem("ac-worktrees-hide-stale");
  // Default true on first visit. Only flip to false when the user
  // has explicitly clicked the toggle off.
  return v === null ? true : v === "1";
})();

// worktreeIconSVG returns the inline SVG used as the leading visual on
// every worktree row. Two intersecting circles = "branches off a
// trunk," same metaphor git uses. Sized to match `.event-row` icons
// across the dashboard so the visual rhythm is consistent.
function worktreeIconSVG() {
  return (
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round">' +
    '<circle cx="6" cy="6" r="3"/>' +
    '<path d="M6 21V9"/>' +
    '<path d="M18 9a9 9 0 0 1-9 9"/>' +
    '<circle cx="18" cy="6" r="3"/>' +
    "</svg>"
  );
}

// renderWorktreeRow renders one row for the Worktrees page. Shared
// between cloud-mode and local-mode renderers so the visual treatment
// stays in sync. The layout is icon → main column → timestamp:
//
//   [svg]  title-line   (chips on the right)         [time]
//          path
//          metadata · separators · between · pieces
//
// Input contract:
//   - Plain string fields (branch, path, repo, machine, ageLabel) are
//     escaped by this function. Pass them raw.
//   - HTML-content fields (stateBadge, sourceBadge, actionsHTML,
//     iconSvg) are interpolated as-is. Callers MUST construct them
//     from escapeHTML'd values — any user-supplied string going into
//     one of these slots must be escaped at the call site.
function renderWorktreeRow(opts) {
  const branch = escapeHTML(opts.branch || "(no branch)");
  const path = escapeHTML(opts.path || "");
  const repo = escapeHTML(opts.repo || "");
  const machine = escapeHTML(opts.machine || "");
  const ageLabel = escapeHTML(opts.ageLabel || "");
  // Build the metadata strip with separator middots only between
  // populated fields so empty machine/repo don't produce floating
  // bullets.
  const metaParts = [];
  if (machine) {
    metaParts.push(
      '<span><code style="font-size: 11px;">' + machine + "</code></span>",
    );
  }
  if (repo) {
    metaParts.push(
      '<span style="color: var(--text-muted);">repo:</span> ' +
        '<span style="font-family: var(--font-mono); font-size: 11px;">' +
        repo +
        "</span>",
    );
  }
  const metaStrip = metaParts.join(
    '<span style="color: var(--text-tertiary);">&middot;</span>',
  );
  const actions = opts.actionsHTML
    ? '<div class="row-card-actions">' + opts.actionsHTML + "</div>"
    : "";
  return (
    '<div class="row-card">' +
    '<div class="row-card-icon" style="color: var(--accent);">' +
    opts.iconSvg +
    "</div>" +
    '<div class="row-card-body">' +
    '<div class="row-card-titleline">' +
    '<strong class="row-card-title">' +
    branch +
    "</strong>" +
    '<div class="row-card-chips">' +
    (opts.stateBadge || "") +
    (opts.sourceBadge || "") +
    "</div>" +
    "</div>" +
    (path
      ? '<div class="row-card-subtitle">' + path + "</div>"
      : "") +
    (metaStrip
      ? '<div class="row-card-meta">' + metaStrip + "</div>"
      : "") +
    "</div>" +
    '<div class="row-card-aside">' +
    '<span class="row-card-time">' +
    ageLabel +
    "</span>" +
    actions +
    "</div>" +
    "</div>"
  );
}

// activityBadgeHTML returns a chip describing how recently the
// worktree was touched. Falls back to created_at when last_activity_at
// is missing (legacy daemons pre-migration-031, or a worktree with no
// commits yet). Four buckets:
//   <1h                       success-green   "active"
//   <24h                      neutral         "today"
//   <STALE_THRESHOLD_DAYS     neutral         "this week"
//   older                     warning-amber   "stale"
//
// Both inputs are RFC3339 ISO strings. Date.parse handles either, and
// NaN comparisons fall through to the "unknown" branch which surfaces
// a plain neutral chip rather than guessing wrong.
function activityBadgeHTML(lastActivityAt, createdAt) {
  const ts = (lastActivityAt && Date.parse(lastActivityAt)) || (createdAt && Date.parse(createdAt));
  if (!ts || isNaN(ts)) {
    return '<span class="type-badge tb-released" data-tooltip="No activity timestamp yet">unknown</span>';
  }
  const ageMs = Date.now() - ts;
  const HOUR = 3600 * 1000;
  const DAY = 24 * HOUR;
  if (ageMs < HOUR) {
    return '<span class="type-badge tb-acquired" data-tooltip="Last commit within the hour">active</span>';
  }
  if (ageMs < DAY) {
    return '<span class="type-badge tb-released" data-tooltip="Last commit within the day">today</span>';
  }
  if (ageMs < STALE_THRESHOLD_DAYS * DAY) {
    return '<span class="type-badge tb-released" data-tooltip="Last commit within the week">this week</span>';
  }
  // tb-expired = warning-amber. Subtler than red; says "you probably
  // want to clean this up" without sounding the alarm bells.
  return `<span class="type-badge tb-expired" data-tooltip="No commit in over ${STALE_THRESHOLD_DAYS} days">stale</span>`;
}

// renderCloudWorktrees is the cloud-mode variant — pulls
// /v1/teams/worktrees from the coordinator and renders an
// active-worktrees-across-team list. Path-bearing fields are HMAC-
// encrypted; we decrypt client-side via state.hmacKey (which the
// daemon's hostkey grants us via the Worker auth flow).
async function renderCloudWorktrees() {
  const body = document.getElementById("worktrees-body");
  if (!body) return;
  let data;
  try {
    const scope = worktreesState.scope === "active" ? "active=true&" : "";
    data = await api(withBuFilter("/v1/teams/worktrees?" + scope + "limit=500"));
  } catch (e) {
    // api() throws "unauthorized: no auth token available" (or just
    // "unauthorized") when the user isn't signed in. Show an explicit
    // sign-in CTA instead of the generic error empty-state — the
    // generic message reads as "the system is broken" when it's
    // actually a normal "please log in" state.
    const msg = (e && e.message) || "fetch failed";
    if (/unauthorized/i.test(msg)) {
      patchHTML(
        body,
        `<div class="empty" style="padding: 40px 24px; text-align: center;">
          <div class="empty-title">Sign in to see team worktrees</div>
          <div class="empty-detail">Worktrees are coordinated across your team — sign in with your AgentCollision account to view them.</div>
        </div>`,
      );
      return;
    }
    patchHTML(
      body,
      '<div class="empty"><div class="empty-title">Could not load team worktrees</div><div class="empty-detail">' +
        escapeHTML(msg) +
        "</div></div>",
    );
    return;
  }
  const rows = (data && data.worktrees) || [];
  // Sidebar badge update is deferred to AFTER the hide-stale filter
  // below — see the badge.textContent assignment near the visible/
  // staleCount computation. Putting it here would make the nav badge
  // disagree with the page (badge from coordinator's raw count, page
  // from filtered subset).
  if (rows.length === 0) {
    // Reframe the empty state. With auto-discovery shipped (PR #107),
    // any git worktree in a repo where AC has hook activity surfaces
    // here automatically — no allocator setup required. So an empty
    // list usually means one of:
    //   - the user simply doesn't use git worktrees (common!), or
    //   - the daemon hasn't done its first discovery scan yet
    //     (~15s grace period at boot, then every 5min).
    // Lead with "this is optional," then explain what worktrees are
    // for so curious users can decide to try them.
    patchHTML(
      body,
      `<div class="empty" style="padding: 40px 24px; text-align: left; max-width: 640px; margin: 0 auto;">
        <div class="empty-title" style="text-align: center;">No worktrees yet</div>
        <div class="empty-detail" style="margin-top: var(--s4); line-height: 1.6;">
          <p style="margin-bottom: var(--s3);"><strong>Worktrees are optional.</strong> Not every workflow uses them &mdash; many teams ship perfectly well with a single checkout per repo.</p>
          <p style="margin-bottom: var(--s3); color: var(--text-secondary);">If you do work in multiple branches at once, git worktrees let each parallel chat have its own isolated checkout (no stashing, no branch-switching). AgentCollision auto-detects worktrees in any repo where its hooks have fired &mdash; you don't have to configure anything.</p>
          <p style="margin-bottom: var(--s3); color: var(--text-secondary);">If you do use them and don't see them here:</p>
          <ol style="padding-left: 22px; line-height: 1.7; color: var(--text-secondary);">
            <li>Confirm the daemon is running on your machine: <code>agc status</code>.</li>
            <li>Make sure you've opened a Claude Code session in the repo at least once so AC knows about it.</li>
            <li>Run <code>agc worktree scan</code> to trigger discovery immediately instead of waiting for the next 5-minute tick.</li>
          </ol>
          <p style="margin-top: var(--s4); font-size: 11px; color: var(--text-tertiary);">Learn more: <a href="https://docs.agentcollision.com/worktrees" style="color: var(--accent);">Worktrees in AgentCollision</a>.</p>
        </div>
      </div>`,
    );
    return;
  }
  // Decrypt hints client-side. Plaintext repo_root + worktree_path
  // never crosses the wire — coordinator only saw the encrypted
  // hints. Browser decrypts using state.hmacKey.
  const decrypted = await Promise.all(
    rows.map(async (r) => ({
      ...r,
      repo_root: r.repo_root_hint
        ? (await decryptHint(r.repo_root_hint)) || "(encrypted)"
        : "",
      worktree_path: r.worktree_path_hint
        ? (await decryptHint(r.worktree_path_hint)) || "(encrypted)"
        : "",
      branch_name: r.branch_hint
        ? (await decryptHint(r.branch_hint)) || ""
        : "",
    })),
  );

  // Apply the hide-stale filter. The coordinator's `active=true`
  // wire filter only excludes formally-released worktrees, but the
  // page inherits a long tail of SessionStart-allocated checkouts
  // that look "active" by the wire definition but haven't been
  // touched in weeks. Mirror activityBadgeHTML's threshold so the
  // "stale" chip and "hidden by default" are aligned.
  const STALE_THRESHOLD_MS = STALE_THRESHOLD_DAYS * 24 * 3600 * 1000;
  const isStale = (w) => {
    const ts = (w.last_activity_at && Date.parse(w.last_activity_at)) ||
      (w.created_at && Date.parse(w.created_at));
    if (!ts || isNaN(ts)) return false; // unknown → don't hide
    return Date.now() - ts >= STALE_THRESHOLD_MS;
  };
  const staleCount = decrypted.filter(isStale).length;
  const visible = hideStale ? decrypted.filter((w) => !isStale(w)) : decrypted;

  // Sidebar #nav-worktrees-count badge reflects what the user sees on
  // the page — visible.length when hide-stale is on (default), full
  // count otherwise. fetchers.js's fetchWorktrees() handles the local-
  // daemon path but never updates this badge for cloud mode, and the
  // coordinator's raw active_count doesn't know about the client-side
  // stale filter — without this sync, the nav badge and page disagree.
  const badge = document.getElementById("nav-worktrees-count");
  if (badge && worktreesState.scope === "active") badge.textContent = String(visible.length);

  const machineCount = new Set(visible.map((r) => r.machine_id)).size;
  const toggleLabel = hideStale
    ? `Show ${staleCount} stale`
    : `Hide ${staleCount} stale`;
  const toggleButton =
    staleCount > 0
      ? `<button id="worktrees-toggle-stale" style="margin-left: var(--s3); background: transparent; border: 1px solid var(--border); color: var(--text-secondary); padding: 2px 8px; border-radius: var(--r-sm); font-size: 11px; cursor: pointer;">${escapeHTML(toggleLabel)}</button>`
      : "";
  let html = `
    <div style="padding: 10px 16px; font-size: 12px; color: var(--text-muted); border-bottom: 1px solid var(--border); display: flex; align-items: center;">
      <span><strong>${visible.length}</strong> ${worktreesState.scope === "active" ? "active " : ""}worktrees across <strong>${machineCount}</strong> machine${machineCount === 1 ? "" : "s"}${staleCount > 0 && hideStale ? ` <span style="color: var(--text-tertiary);">(${staleCount} stale hidden)</span>` : ""}</span>
      ${toggleButton}
    </div>`;
  for (const w of visible.filter(matchesWorktreeFilter)) {
    // Worktrees the daemon DIDN'T allocate (i.e. created by a project
    // SessionStart hook or by hand) get a "discovered" badge instead
    // of the chat-session agent name. They come up via the daemon's
    // periodic `git worktree list` scan. See isDiscoveredWorktree for why
    // this can't key on agent_id alone.
    const isDiscovered = isDiscoveredWorktree(w);
    // tb-released = neutral muted chip (matches the canonical
    // discovered look). tb-acquired = success-bg/success-text chip,
    // readable on the dark theme — replaces the previous hardcoded
    // "var(--success-soft, #dcfce7)" which rendered as washed-out
    // mint with white text. Now consistent with Events/Coordination
    // page chip styling.
    const sourceBadge = isDiscovered
      ? '<span class="type-badge tb-released" data-tooltip="Found by git worktree scan. Not allocated by AC itself.">discovered</span>'
      : '<span class="type-badge tb-released">' + escapeHTML(w.agent_id) + '</span>';
    html += renderWorktreeRow({
      iconSvg: worktreeIconSVG(),
      branch: w.branch_name,
      // Activity-bucketed badge — replaces the always-"active" chip.
      // See activityBadgeHTML for the bucketing rules.
      stateBadge: activityBadgeHTML(w.last_activity_at, w.created_at),
      sourceBadge,
      ageLabel: ago(w.created_at),
      path: w.worktree_path,
      machine: (w.machine_id || "").slice(0, 12),
      repo: w.repo_root,
    });
  }
  patchHTML(body, html);
}

async function renderWorktreesPage() {
  if (isCloudMode()) {
    await renderCloudWorktrees();
    return;
  }
  if (worktreesState.list === null && !worktreesState.error) {
    await fetchWorktrees();
  }
  const body = document.getElementById("worktrees-body");
  if (!body) return;
  // Another render's fetch is still in flight (fetchWorktrees returned at
  // once): keep "Loading worktrees…" up; that render paints the result.
  if (worktreesState.list === null && !worktreesState.error) return;
  if (worktreesState.error) {
    patchHTML(
      body,
      '<div class="empty">' +
        '<div class="empty-title">Could not load worktrees</div>' +
        '<div class="empty-detail">' +
        escapeHTML(worktreesState.error) +
        "</div></div>",
    );
    return;
  }
  let rows = (worktreesState.list || []).slice();
  rows.sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  );
  const q = (worktreesState.filter || "").trim();
  rows = rows.filter(matchesWorktreeFilter);
  if (!rows.length) {
    patchHTML(
      body,
      '<div class="empty">' +
        '<div class="empty-title">' + (q ? "No worktrees match this filter" : "No worktrees yet") + '</div>' +
        '<div class="empty-detail">Allocation runs on SessionStart for managed repos. ' +
        'Configure it in <a href="#" data-page-link="machine-settings">Settings</a>.</div></div>',
    );
    return;
  }
  let html = "";
  html +=
    '<div style="padding: 10px 16px; font-size: 12px; color: var(--text-muted); border-bottom: 1px solid var(--border);">';
  html +=
    "<strong>" +
    worktreesState.activeCount +
    "</strong> active · <strong>" +
    escapeHTML(fmtDiskMB(worktreesState.totalDiskMB)) +
    "</strong> on disk";
  html += "</div>";
  for (const w of rows) {
    const released = !!w.released_at;
    // Released keeps the "released" chip — that's a strong signal
    // the daemon explicitly closed the worktree. For active rows,
    // bucket by last_activity_at so users can spot stale-on-disk
    // worktrees they should clean up. Field names differ between
    // local-mode (LastActivityAt, CreatedAt) and cloud-mode
    // (last_activity_at, created_at); accept both.
    const lastActivity = w.last_activity_at || w.LastActivityAt || "";
    const created = w.created_at || w.CreatedAt || "";
    const stateBadge = released
      ? '<span class="type-badge tb-released">released</span>'
      : activityBadgeHTML(lastActivity, created);
    const sourceBadge =
      isDiscoveredWorktree(w)
        ? '<span class="type-badge tb-released" data-tooltip="' +
          escapeHTML(
            "Found by git worktree scan. Not allocated by AC itself." +
              (w.agent_id && w.agent_id !== "ac:discovered"
                ? " In use by " + w.agent_id + "."
                : " No session seen working in it yet."),
          ) +
          '">discovered</span>'
        : '<span class="type-badge tb-released">' +
          escapeHTML(w.agent_id || "") +
          "</span>";
    // No Release button: the bootstrap.js handler that backed
    // `[data-worktree-release]` was deleted as dead code in commit
    // 420a5ee (2026-05-13). The button rendered fine but clicked to
    // nothing. Surface `agc worktree release <id>` from the CLI for
    // now; we'll add a working dashboard control when the underlying
    // /v1/worktrees/{id} DELETE endpoint gets a proper UX (confirm
    // dialog, in-flight state, error handling). Hidden < broken.
    const actionsHTML = "";
    html += renderWorktreeRow({
      iconSvg: worktreeIconSVG(),
      branch: w.branch_name,
      stateBadge,
      sourceBadge,
      ageLabel: ago(w.created_at),
      path: w.worktree_path,
      machine: w.worktree_id,
      repo: w.repo_root,
      actionsHTML,
    });
  }
  patchHTML(body, html);
}

// ---- mount ----

let started = false;
let tickHandler = null;
let toggleHandler = null;

function start() {
  if (started) return;
  started = true;
  // Initial render fires immediately on activation. The earlier
  // version deferred the first render to the next `dashboard:tick`,
  // which is fine on local-daemon mode where the tick loop fires on
  // every SSE event — but cloud-pages mode has no SSE feeding the
  // tick loop, so the page sat on "Loading worktrees…" forever.
  // Fire the render now; subsequent tick handlers re-render to pick
  // up newly-arrived data.
  renderWorktreesPage();
  tickHandler = () => {
    if (state.activePage === "worktrees") renderWorktreesPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);

  // Delegated click handler for the stale-toggle button and Run GC
  // header button. The toggle is re-rendered into the worktrees-body
  // on every render so we can't bind directly to it — delegating
  // from the page section keeps the listener stable across re-renders.
  // The GC button is in the static page header but we share the same
  // delegated handler for symmetry.
  const section = document.querySelector(
    'section.page[data-page="worktrees"]',
  );
  if (section && !toggleHandler) {
    toggleHandler = (e) => {
      if (e.target && e.target.id === "worktrees-toggle-stale") {
        hideStale = !hideStale;
        localStorage.setItem("ac-worktrees-hide-stale", hideStale ? "1" : "0");
        renderWorktreesPage();
        return;
      }
      const chip = e.target && e.target.closest("[data-worktree-state]");
      if (chip) {
        worktreesState.scope = chip.dataset.worktreeState;
        section.querySelectorAll("[data-worktree-state]").forEach((c) => c.classList.toggle("active", c === chip));
        worktreesState.list = null;
        renderWorktreesPage();
        return;
      }
      if (e.target && e.target.id === "worktree-gc-btn") {
        handleRunGC(e.target);
        return;
      }
    };
    section.addEventListener("click", toggleHandler);
    section.addEventListener("input", filterHandler);
  }
}

function filterHandler(e) {
  if (e.target && e.target.id === "worktrees-filter") {
    worktreesState.filter = e.target.value;
    renderWorktreesPage();
  }
}

// matchesWorktreeFilter applies the search box to a row (local or decrypted
// hosted shape).
function matchesWorktreeFilter(w) {
  const q = (worktreesState.filter || "").toLowerCase().trim();
  if (!q) return true;
  return [w.agent_id, w.branch_name, w.repo_root, w.worktree_path, w.worktree_id]
    .some((v) => (v || "").toLowerCase().includes(q));
}

// handleRunGC POSTs to /v1/worktrees/gc and surfaces the result via
// a toast. GC removes clean worktrees whose owning agent hasn't sent
// a heartbeat in over an hour, preserving any with uncommitted changes
// (settings.WorktreePreserveDirty, default true). Cloud-mode dashboards
// have no daemon backing this endpoint, so we short-circuit with a hint.
async function handleRunGC(btn) {
  if (isCloudMode()) {
    showToast(
      "error",
      "Clean-up runs on each computer. Run `agc worktree gc` on the computer that has these work folders.",
    );
    return;
  }
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Cleaning up…";
  try {
    const resp = await apiFetch("/v1/worktrees/gc", { method: "POST" });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || "HTTP " + resp.status);
    }
    const data = await resp.json().catch(() => ({}));
    const removed = data.removed || 0;
    const preserved = data.preserved || 0;
    const freedMB = data.freed_mb || 0;
    const errs = Array.isArray(data.errors) ? data.errors.length : 0;
    let summary;
    if (removed === 0 && preserved === 0) {
      summary = "Nothing to clean up.";
    } else {
      summary =
        "Removed " +
        removed +
        ", preserved " +
        preserved +
        (freedMB > 0 ? " (" + freedMB + " MB freed)" : "");
    }
    if (errs > 0) {
      summary += " · " + errs + " error" + (errs === 1 ? "" : "s");
    }
    // Force a re-fetch so the list reflects the GC result.
    worktreesState.list = null;
    renderWorktreesPage();
    showToast(errs > 0 ? "error" : "success", summary);
  } catch (err) {
    showToast("error", "Clean-up failed: " + (err.message || err));
  } finally {
    btn.disabled = false;
    btn.textContent = orig;
  }
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
  if (toggleHandler) {
    const section = document.querySelector(
      'section.page[data-page="worktrees"]',
    );
    if (section) {
      section.removeEventListener("click", toggleHandler);
      section.removeEventListener("input", filterHandler);
    }
    toggleHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector(
    'section.page[data-page="worktrees"]',
  );
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}

// Export renderCloudWorktrees so bootstrap.js's bridge entry
// stays valid. Eliminated when the bridge goes away.
export { renderCloudWorktrees };

